<template>
  <div class="shop-info-page">
    <el-card class="content-card" shadow="hover">
      <el-descriptions
        class="info-descriptions"
        :column="2"
        border
        :label-width="110"
        size="medium"
      >
        <el-descriptions-item
          v-for="field in formFields"
          :key="field.prop"
          :label="field.label"
          :span="getSpan(field.type)"
        >
          <!-- 普通弹窗输入 -->
          <template v-if="field.type === 'popup-input' || field.type === 'popup-textarea'">
            <div class="field-value-display" @click="openInputPopup(field.prop, field.label, shopInfo[field.prop], field.maxlength, field.type === 'popup-textarea' ? 'textarea' : (field.inputType || 'text'))">
              <span v-if="shopInfo[field.prop]" class="field-value-text">{{ shopInfo[field.prop] }}</span>
              <span v-else class="field-value-placeholder">{{ field.placeholder }}</span>
              <i class="el-icon-edit-outline field-edit-icon"></i>
            </div>
          </template>

          <!-- 选择器 -->
          <template v-else-if="field.type === 'picker'">
            <el-select
              v-model="shopInfo[field.prop]"
              :placeholder="field.placeholder"
              size="small"
              style="width: 100%;"
              @change="(val) => onPickerChange(field, val)"
            >
              <el-option
                v-for="option in field.options"
                :key="option"
                :label="option"
                :value="option"
              />
            </el-select>
          </template>

          <!-- 多选 -->
          <template v-else-if="field.type === 'multi-select'">
            <div class="multi-select-container" @click="openSalesTypePopup">
              <div class="select-content">
                <div v-if="selectedSalesTypes.length > 0" class="selected-tags">
                  <el-tag
                    v-for="(item, index) in selectedSalesTypes"
                    :key="index"
                    closable
                    size="small"
                    @close="removeSalesType(index)"
                  >
                    {{ item.label }}
                  </el-tag>
                </div>
                <span class="select-btn">
                  <span>{{ selectedSalesTypes.length > 0 ? '点击修改' : '请选择经营范围' }}</span>
                  <i class="el-icon-arrow-right"></i>
                </span>
              </div>
            </div>
          </template>

          <!-- 上传类型（添加 action="" 修复警告） -->
          <template v-else-if="field.type === 'upload'">
            <div class="upload-container">
              <el-upload
                class="upload-demo"
                :ref="field.refName"
                action=""
                :http-request="(options) => handleUpload(options, field)"
                :before-upload="(file) => beforeUpload(file, field)"
                :show-file-list="false"
                accept="image/*"
              >
                <el-button size="small" type="primary" :loading="uploadLoading[field.prop]">
                  点击上传
                </el-button>
                <div slot="tip" class="el-upload__tip">
                  仅支持图片，大小不超过{{ field.uploadType === 'license' ? '5' : '2' }}MB
                </div>
              </el-upload>
              <div v-if="shopInfo[field.prop]" class="uploaded-preview">
                <img :src="shopInfo[field.prop]" alt="预览" />
                <i class="el-icon-delete" @click="removeUpload(field.prop)"></i>
              </div>
            </div>
          </template>

          <!-- 只读 -->
          <template v-else-if="field.type === 'readonly'">
            <span class="field-value-text">{{ shopInfo[field.prop] || field.placeholder }}</span>
          </template>

          <!-- 地区 -->
          <template v-else-if="field.type === 'region'">
            <el-select
              v-model="selectedRegion"
              placeholder="请选择地区"
              size="small"
              style="width: 100%;"
              @change="onRegionChange"
            >
              <el-option
                v-for="item in regionList"
                :key="item.id"
                :label="item.name"
                :value="item.name"
              />
            </el-select>
          </template>

          <!-- 市场 -->
          <template v-else-if="field.type === 'market'">
            <el-select
              v-model="selectedMarket"
              :placeholder="selectedRegion ? '请选择市场' : '请先选择地区'"
              size="small"
              style="width: 100%;"
              :disabled="!selectedRegion"
              @change="onMarketChange"
            >
              <el-option
                v-for="item in marketList"
                :key="item.market_id"
                :label="item.market_name"
                :value="item.market_name"
              />
            </el-select>
          </template>
        </el-descriptions-item>
      </el-descriptions>

      <div class="action-buttons">
        <el-button type="primary" size="medium" :loading="isSubmitting" @click="submitShopInfo">
          {{ isSubmitting ? '提交中...' : '确认' }}
        </el-button>
      </div>
    </el-card>

    <!-- 输入弹窗 -->
    <el-dialog
      :title="inputPopupTitle"
      :visible.sync="inputDialogVisible"
      width="500px"
      :close-on-click-modal="false"
      append-to-body
    >
      <el-input
        v-if="inputPopupType !== 'textarea'"
        v-model="inputPopupValue"
        :placeholder="'请输入' + inputPopupTitle"
        :maxlength="inputPopupMaxLength"
        :type="getInputType(inputPopupType)"
        clearable
        show-word-limit
      />
      <el-input
        v-else
        v-model="inputPopupValue"
        type="textarea"
        :placeholder="'请输入' + inputPopupTitle"
        :maxlength="inputPopupMaxLength"
        :rows="4"
        show-word-limit
      />
      <span slot="footer" class="dialog-footer">
        <el-button @click="closeInputPopup">取消</el-button>
        <el-button type="primary" @click="confirmInputPopup">确定</el-button>
      </span>
    </el-dialog>

    <!-- 经营范围多选弹窗 -->
    <el-dialog
      title="选择经营范围"
      :visible.sync="salesTypeDialogVisible"
      width="500px"
      :close-on-click-modal="false"
      append-to-body
    >
      <el-checkbox-group v-model="tempSelectedSalesTypes">
        <el-checkbox
          v-for="item in salesTypeOptions"
          :key="item.value"
          :label="item.value"
        >
          {{ item.label }}
        </el-checkbox>
      </el-checkbox-group>
      <span slot="footer" class="dialog-footer">
        <el-button @click="closeSalesTypePopup">取消</el-button>
        <el-button type="primary" @click="confirmSalesType">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import request from '@/utils/request'
import axios from 'axios'

export default {
  name: 'ShopInfo',
  data () {
    return {
      formFields: [
        { prop: 'sh_name', label: '店铺名称', type: 'popup-input', placeholder: '请输入店铺名称', maxlength: 100, required: true },
        { prop: 'comlist_v1', label: '联系电话', type: 'popup-input', placeholder: '请输入联系电话', maxlength: 11, inputType: 'tel', required: true, rules: [{ required: true, message: '联系电话不能为空' }, { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号' }] },
        { prop: 'mem_img', label: '店铺头像', type: 'upload', buttonText: '上传店铺图片', refName: 'logoUploadRef', uploadType: 'logo' },
        { prop: 'zhi_img', label: '证件上传', type: 'upload', buttonText: '上传证件', refName: 'licenseUploadRef', uploadType: 'license' },
        { prop: 'comlist_v5', label: '销售类型', type: 'readonly', placeholder: '销售类型' },
        { prop: 'comlist_m1', label: '店铺公告', type: 'popup-textarea', placeholder: '请输入店铺公告', maxlength: 1000 },
        { prop: 'comlist_v3', label: '档口号', type: 'popup-input', placeholder: '请输入档口号', maxlength: 50 },
        { prop: 'comlist_v4', label: '店铺地址', type: 'popup-textarea', placeholder: '请输入店铺地址', maxlength: 200 },
        { prop: 'comlist_v6', label: '经营范围', type: 'multi-select', placeholder: '请选择经营范围' },
        { prop: 'comlist_v7', label: '店铺推荐人', type: 'popup-input', placeholder: '请输入店铺推荐人', maxlength: 50 },
        { prop: 'comlist_v8', label: '店铺类型', type: 'picker', options: ['企业', '个体工商户', '小微商户'], placeholder: '请选择店铺类型' },
        { prop: 'comlist_v9', label: '企业名称', type: 'popup-textarea', placeholder: '请输入企业名称', maxlength: 100 },
        { prop: 'comlist_v10', label: '统一社会信用代码', type: 'popup-input', placeholder: '请输入统一社会信用代码', maxlength: 18, inputType: 'idcard' },
        { prop: 'comlist_v11', label: '证件有效期', type: 'popup-input', placeholder: '请输入证件有效期', maxlength: 50 },
        { prop: 'comlist_v12', label: '企业地址', type: 'popup-textarea', placeholder: '请输入企业地址', maxlength: 200 },
        { prop: 'comlist_v13', label: '法人姓名', type: 'popup-input', placeholder: '请输入法人姓名', maxlength: 20 },
        { prop: 'comlist_v14', label: '身份证号', type: 'popup-input', placeholder: '请输入身份证号', maxlength: 18, inputType: 'idcard' },
        { prop: 'comlist_v15', label: '身份证有效期', type: 'popup-input', placeholder: '请输入身份证有效期', maxlength: 50 }
      ],

      uploadLoading: { mem_img: false, zhi_img: false },
      isSubmitting: false,
      isLoading: false,

      salesTypeOptions: [
        { label: '水果', value: '水果' },
        { label: '蔬菜', value: '蔬菜' },
        { label: '冻品', value: '冻品' },
        { label: '水产', value: '水产' },
        { label: '菌菇', value: '菌菇' },
        { label: '肉禽蛋奶', value: '肉禽蛋奶' },
        { label: '副食饮料', value: '副食饮料' },
        { label: '粮油调味', value: '粮油调味' },
        { label: '其他', value: '其他' }
      ],
      tempSelectedSalesTypes: [],

      regionList: [
        { id: 1, name: '北京市' }, { id: 2, name: '天津市' }, { id: 3, name: '河北省' },
        { id: 4, name: '山西省' }, { id: 5, name: '内蒙古自治区' }, { id: 6, name: '辽宁省' },
        { id: 7, name: '吉林省' }, { id: 8, name: '黑龙江省' }, { id: 9, name: '上海市' },
        { id: 10, name: '江苏省' }, { id: 11, name: '浙江省' }, { id: 12, name: '安徽省' },
        { id: 13, name: '福建省' }, { id: 14, name: '江西省' }, { id: 15, name: '山东省' },
        { id: 16, name: '河南省' }, { id: 17, name: '湖北省' }, { id: 18, name: '湖南省' },
        { id: 19, name: '广东省' }, { id: 20, name: '广西壮族自治区' }, { id: 21, name: '海南省' },
        { id: 22, name: '重庆市' }, { id: 23, name: '四川省' }, { id: 24, name: '贵州省' },
        { id: 25, name: '云南省' }, { id: 26, name: '西藏自治区' }, { id: 27, name: '陕西省' },
        { id: 28, name: '甘肃省' }, { id: 29, name: '青海省' }, { id: 30, name: '宁夏回族自治区' },
        { id: 31, name: '新疆维吾尔自治区' }, { id: 32, name: '台湾省' }, { id: 33, name: '香港特别行政区' },
        { id: 34, name: '澳门特别行政区' }
      ],
      marketList: [],
      selectedRegion: '',
      selectedMarket: '',
      selectedRegionId: '',
      selectedMarketId: '',

      inputDialogVisible: false,
      inputPopupTitle: '',
      inputPopupValue: '',
      inputPopupField: '',
      inputPopupMaxLength: 0,
      inputPopupType: 'text',

      salesTypeDialogVisible: false,

      shopInfo: {
        sh_name: '',
        comlist_v1: '',
        mem_img: '',
        zhi_img: '',
        comlist_v2: '',
        comlist_v2_1: '',
        comlist_v2_2: '',
        comlist_v3: '',
        comlist_v4: '',
        comlist_v5: '',
        comlist_v6: '',
        comlist_v7: '',
        comlist_v8: '',
        comlist_v9: '',
        comlist_v10: '',
        comlist_v11: '',
        comlist_v12: '',
        comlist_m1: '',
        comlist_v13: '',
        comlist_v14: '',
        comlist_v15: ''
      }
    }
  },

  computed: {
    selectedSalesTypes () {
      if (!this.shopInfo.comlist_v6) return []
      const selectedValues = this.shopInfo.comlist_v6.split(',').filter(v => v)
      return this.salesTypeOptions.filter(item => selectedValues.includes(item.value))
    }
  },

  mounted () {
    this.fetchShopInfo()
  },

  methods: {
    getSpan (type) {
      return ['upload', 'multi-select', 'popup-textarea'].includes(type) ? 2 : 1
    },

    // 图片上传校验
    beforeUpload (file, field) {
      const isImage = file.type.startsWith('image/')
      const maxSize = field.uploadType === 'license' ? 5 : 2
      const isLtSize = file.size / 1024 / 1024 < maxSize
      if (!isImage) {
        this.$message.error('只能上传图片文件')
        return false
      }
      if (!isLtSize) {
        this.$message.error(`图片大小不能超过 ${maxSize}MB`)
        return false
      }
      return true
    },

    // 自定义上传
    async handleUpload (options, field) {
      const file = options.file
      const formData = new FormData()
      formData.append('file', file)
      formData.append('comlist2', localStorage.getItem('user_name') || '')
      formData.append('comlistToken', localStorage.getItem('token') || '')

      const url = field.uploadType === 'license'
        ? 'https://caixiao.lianglianyn.com/index/upload_dian_zhi.php'
        : 'https://caixiao.lianglianyn.com/index/upload_dian.php'

      this.uploadLoading[field.prop] = true
      try {
        const res = await axios.post(url, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            comlistToken: localStorage.getItem('token') || ''
          }
        })
        if (res.data && res.data.code === 0) {
          let imgUrl = ''
          if (field.uploadType === 'logo') {
            imgUrl = res.data.yasuo_url || res.data.url
          } else {
            imgUrl = res.data.url
          }
          if (imgUrl) {
            this.shopInfo[field.prop] = imgUrl
            this.$message.success(`${field.label}上传成功`)
          } else {
            this.$message.error('上传失败：未获取到图片地址')
          }
        } else {
          this.$message.error(res.data?.msg || '上传失败')
        }
      } catch (error) {
        console.error('上传失败', error)
        this.$message.error('网络错误，请重试')
      } finally {
        this.uploadLoading[field.prop] = false
      }
    },

    // 删除图片
    removeUpload (prop) {
      this.shopInfo[prop] = ''
    },

    onPickerChange (field, value) {},

    async getMarketList (region) {
      try {
        const data = await request.post('/index/get_region_list.php', { name: region })
        this.marketList = data?.data || []
      } catch (error) {
        console.error('获取市场列表失败:', error)
        this.marketList = []
      }
    },

    onRegionChange (val) {
      const selected = this.regionList.find(item => item.name === val)
      if (selected) {
        this.selectedRegion = selected.name
        this.selectedRegionId = selected.id
        this.selectedMarket = ''
        this.selectedMarketId = ''
        this.marketList = []
        this.getMarketList(this.selectedRegion)
        this.shopInfo.comlist_v2 = ''
        this.shopInfo.comlist_v2_1 = ''
        this.shopInfo.comlist_v2_2 = this.selectedRegion
      }
    },

    onMarketChange (val) {
      const selected = this.marketList.find(item => item.market_name === val)
      if (selected) {
        this.selectedMarket = selected.market_name
        this.selectedMarketId = selected.market_id
        this.shopInfo.comlist_v2 = this.selectedMarket
        this.shopInfo.comlist_v2_1 = this.selectedMarketId
        this.shopInfo.comlist_v2_2 = this.selectedRegion
      }
    },

    parseExistingMarketInfo (marketStr) {
      if (!marketStr) return
      const parts = marketStr.split('-')
      if (parts.length >= 2) {
        const regionName = parts[0]
        const marketName = parts[1]
        const region = this.regionList.find(item => item.name === regionName)
        if (region) {
          this.selectedRegion = regionName
          this.selectedRegionId = region.id
          this.getMarketList(this.selectedRegion).then(() => {
            const market = this.marketList.find(item => item.market_name === marketName)
            if (market) {
              this.selectedMarket = marketName
              this.selectedMarketId = market.market_id
              this.shopInfo.comlist_v2 = marketName
              this.shopInfo.comlist_v2_1 = market.market_id
              this.shopInfo.comlist_v2_2 = regionName
            }
          })
        }
      }
    },

    async fetchShopInfo () {
      this.isLoading = true
      const shId = localStorage.getItem('sh_id')
      const userType = localStorage.getItem('user_type')
      try {
        const data = await request.post('/admin/dianpu_jiben_list.php', { sh_id: shId, user_type: userType })
        if (data && data.code === 0 && data.dianList) {
          const detail = data.dianList
          this.shopInfo = {
            comlist2: detail.comlist2,
            sh_name: detail.sh_name || detail.mem_dian || '',
            comlist_v1: detail.comlist_v1 || detail.comlist2 || '',
            mem_img: detail.sh_img || '',
            zhi_img: detail.zhi_img || '',
            comlist_v2: detail.comlist_v2 || '',
            comlist_v2_1: detail.comlist_v2_1 || '',
            comlist_v2_2: detail.comlist_v2_2 || '',
            comlist_v3: detail.comlist_v3 || '',
            comlist_v4: detail.comlist_v4 || '',
            comlist_v5: detail.comlist_v6 || '',
            comlist_v6: detail.comlist_v5 || '',
            comlist_v7: detail.comlist_v7 || '',
            comlist_v8: detail.comlist_v8 || '',
            comlist_v9: detail.comlist_v9 || '',
            comlist_v10: detail.comlist_v10 || '',
            comlist_v11: detail.comlist_v11 || '',
            comlist_v12: detail.comlist_v12 || '',
            comlist_m1: detail.comlist_m1 || '',
            comlist_v13: detail.comlist_v13 || '',
            comlist_v14: detail.comlist_v14 || '',
            comlist_v15: detail.comlist_v15 || ''
          }
          const comlistv2 = `${this.shopInfo.comlist_v2_2}-${this.shopInfo.comlist_v2}`
          this.parseExistingMarketInfo(comlistv2)
          localStorage.setItem('shop_name', this.shopInfo.sh_name)
          localStorage.setItem('currentShopName', this.shopInfo.sh_name)
        } else {
          this.$message.error(data?.msg || '获取店铺信息失败')
        }
      } catch (error) {
        console.error('请求失败:', error)
        this.$message.error(error.message || '网络错误，请重试')
      } finally {
        this.isLoading = false
      }
    },

    openSalesTypePopup () {
      this.tempSelectedSalesTypes = this.shopInfo.comlist_v6 ? this.shopInfo.comlist_v6.split(',').filter(v => v) : []
      this.salesTypeDialogVisible = true
    },
    closeSalesTypePopup () {
      this.salesTypeDialogVisible = false
    },
    confirmSalesType () {
      this.shopInfo.comlist_v6 = this.tempSelectedSalesTypes.join(',')
      this.closeSalesTypePopup()
    },
    removeSalesType (index) {
      const selectedValues = this.shopInfo.comlist_v6.split(',').filter(v => v)
      selectedValues.splice(index, 1)
      this.shopInfo.comlist_v6 = selectedValues.join(',')
    },

    openInputPopup (field, title, value, maxLength = 50, type = 'text') {
      this.inputPopupField = field
      this.inputPopupTitle = title
      this.inputPopupValue = value || ''
      this.inputPopupMaxLength = maxLength
      this.inputPopupType = type
      this.inputDialogVisible = true
    },
    closeInputPopup () {
      this.inputDialogVisible = false
    },
    confirmInputPopup () {
      if (this.inputPopupField === 'comlist_v1' && !/^1[3-9]\d{9}$/.test(this.inputPopupValue)) {
        this.$message.error('请输入正确的手机号')
        return
      }
      this.shopInfo[this.inputPopupField] = this.inputPopupValue
      this.closeInputPopup()
    },
    getInputType (type) {
      switch (type) {
        case 'tel': return 'number'
        case 'idcard': return 'text'
        case 'textarea': return 'textarea'
        default: return 'text'
      }
    },

    async submitShopInfo () {
      const requiredFields = this.formFields.filter(f => f.required)
      for (const field of requiredFields) {
        if (!this.shopInfo[field.prop]) {
          this.$message.error(`${field.label}不能为空`)
          return
        }
        if (field.prop === 'comlist_v1' && field.rules) {
          const phoneRule = field.rules.find(r => r.pattern)
          if (phoneRule && !phoneRule.pattern.test(this.shopInfo[field.prop])) {
            this.$message.error(phoneRule.message)
            return
          }
        }
      }
      this.isSubmitting = true
      try {
        const shId = localStorage.getItem('sh_id')
        const userName = localStorage.getItem('user_name')
        const params = {
          sh_id: shId,
          comlist2: userName,
          sh_name: this.shopInfo.sh_name,
          comlist_v1: this.shopInfo.comlist_v1,
          sh_img: this.shopInfo.mem_img,
          zhi_img: this.shopInfo.zhi_img,
          comlist_v2: this.shopInfo.comlist_v2,
          comlist_v2_1: this.shopInfo.comlist_v2_1,
          comlist_v2_2: this.shopInfo.comlist_v2_2,
          comlist_v3: this.shopInfo.comlist_v3,
          comlist_v4: this.shopInfo.comlist_v4,
          comlist_v5: this.shopInfo.comlist_v6,
          comlist_v6: this.shopInfo.comlist_v5,
          comlist_v7: this.shopInfo.comlist_v7,
          comlist_v8: this.shopInfo.comlist_v8,
          comlist_v9: this.shopInfo.comlist_v9,
          comlist_v10: this.shopInfo.comlist_v10,
          comlist_v11: this.shopInfo.comlist_v11,
          comlist_v12: this.shopInfo.comlist_v12,
          comlist_m1: this.shopInfo.comlist_m1,
          comlist_v13: this.shopInfo.comlist_v13,
          comlist_v14: this.shopInfo.comlist_v14,
          comlist_v15: this.shopInfo.comlist_v15
        }
        const data = await request.post('/admin/dianpu_jiben_edit.php', params)
        if (data && data.code === 0) {
          this.$message.success('保存成功')
          localStorage.setItem('sh_img', this.shopInfo.mem_img)
          localStorage.setItem('currentShopName', this.shopInfo.sh_name)
          setTimeout(() => {
            this.$router.push({ path: '/dashboard' })
          }, 500)
        } else {
          this.$message.error(data?.msg || '保存失败，请重试')
        }
      } catch (error) {
        console.error('提交失败:', error)
        this.$message.error('网络错误，请重试')
      } finally {
        this.isSubmitting = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.shop-info-page {
  background-color: #f0f2f5;
  height: 86vh;
  overflow-y: auto;
  padding: 20px;
  box-sizing: border-box;
}

.content-card {
  margin: 0 auto;
  .el-card__body {
    padding: 20px 30px;
  }
}

.info-descriptions {
  .el-descriptions__body .el-descriptions__table {
    table-layout: fixed;
  }

  .field-value-display {
    display: flex;
    align-items: center;
    min-height: 32px;
    padding: 0 4px;
    border-radius: 4px;
    cursor: pointer;
    transition: background 0.2s;
    &:hover {
      background: #f5f7fa;
    }
    .field-value-text {
      flex: 1;
      font-size: 14px;
      color: #333;
      word-break: break-all;
    }
    .field-value-placeholder {
      flex: 1;
      color: #bbb;
      font-size: 14px;
    }
    .field-edit-icon {
      color: #909399;
      margin-left: 8px;
      font-size: 16px;
    }
  }

  .multi-select-container {
    cursor: pointer;
    .select-content {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 8px;
      min-height: 32px;
      padding: 4px 0;
      .selected-tags {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
      }
      .select-btn {
        display: inline-flex;
        align-items: center;
        color: #409eff;
        font-size: 14px;
        i {
          margin-left: 4px;
        }
      }
    }
  }

  .upload-container {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 16px;

    .upload-demo {
      .el-upload {
        border: 1px dashed #d9d9d9;
        border-radius: 6px;
        padding: 8px 12px;
        text-align: center;
        transition: border-color 0.3s;
        &:hover {
          border-color: #409eff;
        }
        .el-button {
          margin: 0;
        }
      }
      .el-upload__tip {
        margin-top: 4px;
        font-size: 12px;
        color: #909399;
      }
    }

    .uploaded-preview {
      position: relative;
      display: inline-block;
      img {
        width: 80px;
        height: 80px;
        object-fit: cover;
        border-radius: 6px;
        border: 1px solid #e0e0e0;
      }
      .el-icon-delete {
        position: absolute;
        top: -8px;
        right: -8px;
        background: #f56c6c;
        color: #fff;
        border-radius: 50%;
        padding: 2px;
        font-size: 14px;
        cursor: pointer;
        transition: transform 0.2s;
        &:hover {
          transform: scale(1.15);
        }
      }
    }
  }
}

.action-buttons {
  margin-top: 24px;
  text-align: center;
  .el-button {
    width: 200px;
  }
}

.el-checkbox-group {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 20px;
  .el-checkbox {
    margin-right: 0;
  }
}
</style>
